import React, { createContext, useContext, useState, useEffect } from 'react';
import { readSharedToken, clearSharedAuthTokens } from './utils/authTokenKeys';

const MobileAuthContext = createContext();

export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within MobileAuthProvider');
  }
  return context;
};

const USER_ROLES = {
  ADMIN: 'admin',
  DEPARTMENT_HEAD: 'department_head', 
  TEAM_MEMBER: 'team_member',
  STAFF: 'staff'
};

const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    canManageOrganizations: true,
    canManageUsers: true,
    canManageBilling: true,
    canManageSettings: true,
    canAccessAllFeatures: true,
    canViewAnalytics: true,
    canManageInventory: true,
    canManageBookings: true,
    canManageEvents: true,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.DEPARTMENT_HEAD]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: true,
    canManageInventory: true,
    canManageBookings: true,
    canManageEvents: true,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.TEAM_MEMBER]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: false,
    canManageInventory: true,
    canManageBookings: false,
    canManageEvents: false,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.STAFF]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: false,
    canManageInventory: false,
    canManageBookings: false,
    canManageEvents: false,
    canManageDelivery: false,
    canManageSafety: true
  }
};

function persistInitialPlatformScope(data) {
  const organizations = Array.isArray(data.organizations) ? data.organizations : [];
  if (Array.isArray(data.organizations)) {
    localStorage.setItem('available_organizations', JSON.stringify(organizations));
  }

  const previousOrgId = localStorage.getItem('current_organization_id');
  const nextOrgId = data.default_organization?.id || (organizations.length === 1 ? organizations[0].id : '');
  if (nextOrgId && previousOrgId !== nextOrgId) {
    localStorage.setItem('current_organization_id', nextOrgId);
    localStorage.removeItem('current_location_id');
  } else if (!nextOrgId) {
    localStorage.removeItem('current_organization_id');
    localStorage.removeItem('current_location_id');
  }

  if (nextOrgId && data.default_location?.id && !localStorage.getItem('current_location_id')) {
    localStorage.setItem('current_location_id', data.default_location.id);
  }
}

export const MobileAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Cross-surface SSO: accept the desktop SPA token (`token`) as a fallback so a
  // user logged in via the desktop shell can navigate to /mobile/* without a
  // second login. Persist back as `auth_token` for the mobile flow's own use.
  // Shared with syncQueue.js's own token read via authTokenKeys.js — they'd
  // drifted into two different implementations before (audit-2026-09-07 §2).
  const [token, setToken] = useState(readSharedToken());
  const [role, setRole] = useState(USER_ROLES.STAFF);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Monitor network status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check auth state
    const initAuth = async () => {
      const storedToken = readSharedToken();
      if (storedToken) {
        try {
          // Verify token and get profile
          const response = await fetch('/api/auth/profile', {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          
          if (response.ok) {
            const userProfile = await response.json();
            setUser(userProfile);
            setRole(userProfile.role || USER_ROLES.STAFF);
            setToken(storedToken);
            // Normalise: keep auth_token in sync so subsequent mobile-only code paths work
            try { localStorage.setItem('auth_token', storedToken); } catch { /* ignore */ }
          } else {
            // Token invalid or expired
            logout();
          }
        } catch (error) {
          // Offline — keep current token; mobile shell can continue with cached state
          console.warn('Error verifying token:', error);
          if (navigator.onLine) {
            logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      const { token: newToken, user: userData } = data;

      persistInitialPlatformScope(data);
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      setUser(userData);
      setRole(userData.role || USER_ROLES.STAFF);
      
      return userData;
    } catch (error) {
      throw new Error('Login failed: ' + error.message);
    }
  };

  const logout = async () => {
    // Was: removeItem('auth_token') only. A stale `token` (or `mobileToken`)
    // key left behind would then shadow a LATER user's session on the same
    // shared/kiosk device — readSharedToken() prefers auth_token, but the
    // next mobile login only ever writes auth_token too, so a leftover
    // `token` from a prior desktop-SSO visit outlives this logout and never
    // gets cleared any other way. clearSharedAuthTokens() removes all three
    // (audit-2026-09-07 §2).
    clearSharedAuthTokens();
    setToken(null);
    setUser(null);
    setRole(USER_ROLES.STAFF);
  };

  const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[role]?.[permission] || false;
  };

  const canAccessFeature = (feature) => {
    switch (feature) {
      case 'analytics':
        return hasPermission('canViewAnalytics');
      case 'inventory':
        return hasPermission('canManageInventory');
      case 'bookings':
        return hasPermission('canManageBookings');
      case 'events':
        return hasPermission('canManageEvents');
      case 'delivery':
        return hasPermission('canManageDelivery');
      case 'safety':
        return hasPermission('canManageSafety');
      case 'admin':
        return hasPermission('canManageOrganizations');
      default:
        return false;
    }
  };

  const getMobileNavigation = () => {
    const navigation = [
      {
        name: 'Dashboard',
        icon: 'home',
        path: '/mobile/dashboard',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      },
      {
        name: 'Inventory',
        icon: 'package',
        path: '/mobile/inventory',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER]
      },
      {
        name: 'Bookings',
        icon: 'calendar',
        path: '/mobile/bookings',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      },
      {
        name: 'Delivery',
        icon: 'truck',
        path: '/mobile/delivery',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER]
      },
      {
        name: 'Safety',
        icon: 'shield',
        path: '/mobile/safety',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER, USER_ROLES.STAFF]
      },
      {
        name: 'Events',
        icon: 'users',
        path: '/mobile/events',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      }
    ];

    return navigation.filter(item => item.roles.includes(role));
  };

  const value = {
    user,
    token,
    role,
    loading,
    isOnline,
    login,
    logout,
    hasPermission,
    canAccessFeature,
    getMobileNavigation,
    USER_ROLES,
    ROLE_PERMISSIONS
  };

  return (
    <MobileAuthContext.Provider value={value}>
      {children}
    </MobileAuthContext.Provider>
  );
};
